import React, { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../context/AuthContext';

const EMPLOYEE_COUNT_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '500+'];
const HOW_HEARD_OPTIONS = ['Google', 'Referral', 'Social media', 'Advertisement', 'Event', 'Other'];

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPopup, setShowPopup] = useState(false);
  const [showDemoRequestModal, setShowDemoRequestModal] = useState(false);
  const [demoRequestLoading, setDemoRequestLoading] = useState(false);
  const [demoRequestError, setDemoRequestError] = useState(null);
  const [demoRequestSuccess, setDemoRequestSuccess] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: '',
    companyName: '',
    email: '',
    phone: '',
    numberOfEmployees: '',
    state: '',
    howYouHeardAboutUs: ''
  });
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.GOOGLE_OAUTH_CLIENT_ID || process.env.GOOGLE_CLIENT_ID;

  const persistGoogleCalToken = (token, expiryMs) => {
    try {
      localStorage.setItem('googleCalToken', token || '');
      localStorage.setItem('googleCalTokenExpiry', String(expiryMs || 0));
    } catch (_) {}
  };

  const ensureGsiLoaded = () =>
    new Promise((resolve) => {
      if (window.google && window.google.accounts && window.google.accounts.oauth2) return resolve();
      const id = 'gsi-client';
      if (document.getElementById(id)) {
        const check = () => (window.google && window.google.accounts && window.google.accounts.oauth2) ? resolve() : setTimeout(check, 50);
        check();
        return;
      }
      const s = document.createElement('script');
      s.id = id;
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      document.head.appendChild(s);
    });

  const requestGoogleCalendarToken = (silent = true) =>
    new Promise(async (resolve, reject) => {
      try {
        if (!GOOGLE_CLIENT_ID) return reject(new Error('Missing GOOGLE_CLIENT_ID'));
        await ensureGsiLoaded();
        const gis = window.google && window.google.accounts && window.google.accounts.oauth2;
        if (!gis) return reject(new Error('Google OAuth script not loaded'));
        // If we already have a valid token, skip active prompt
        const storedExpiry = Number(localStorage.getItem('googleCalTokenExpiry') || '0');
        const storedToken = localStorage.getItem('googleCalToken') || null;
        const isTokenValid = storedToken && Date.now() < storedExpiry;
        if (isTokenValid) {
          resolve(storedToken);
          return;
        }
        const tokenClient = gis.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'https://www.googleapis.com/auth/calendar.readonly',
          prompt: silent ? '' : 'consent',
          callback: (resp) => {
            if (resp && resp.access_token) {
              const expiresInSec = Number(resp.expires_in || 3600);
              const expiry = Date.now() + (expiresInSec - 60) * 1000; // refresh 60s early
              persistGoogleCalToken(resp.access_token, expiry);
              resolve(resp.access_token);
            } else {
              reject(new Error('Failed to obtain access token'));
            }
          }
        });
        tokenClient.requestAccessToken({ prompt: silent ? '' : undefined });
      } catch (err) {
        reject(err);
      }
    });

  const handleGoogleSuccess = async (credentialResponse) => {
    console.log('credentialResponse', credentialResponse);
    try {
      // Decode the credential to get user information
      const decodedCredential = jwtDecode(credentialResponse.credential);
      console.log('Decoded credential:', decodedCredential);

      // Send the email to your backend using the api service
      const response = await api.post('/api/company/login', {
        email: decodedCredential.email
      });

      // Store the token and update auth state with user and company data
      login(
        response.data.token,
        response.data.user,
        response.data.company
      );
      // Try to silently fetch Google Calendar access token for later usage
      try { await requestGoogleCalendarToken(true); } catch (_) {}
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
      // Handle error appropriately
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response:', error.response.data);
        
        // Check if the error is "Email not found"
        if (error.response.status === 404 && error.response.data.message === 'Email not found.') {
          setShowPopup(true);
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', error.message);
      }
    }
  };

  const handleGoogleError = () => {
    console.error('Google login failed');
    // Handle error appropriately
  };

  const handleAppleSuccess = async (response) => {
    console.log('Apple response:', response);
    try {
      // Apple Sign-In returns the user's email in the authorization object
      const email = response.authorization?.id_token ? 
        jwtDecode(response.authorization.id_token).email : 
        response.user?.email;

      if (!email) {
        console.error('No email received from Apple Sign-In');
        return;
      }

      console.log('Apple email:', email);

      // Send the email to your backend using the same endpoint as Google
      const apiResponse = await api.post('/api/company/login', {
        email: email
      });

      // Store the token and update auth state with user and company data
      login(
        apiResponse.data.token,
        apiResponse.data.user,
        apiResponse.data.company
      );
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Apple login failed:', error);
      // Handle error appropriately
      if (error.response) {
        console.error('Error response:', error.response.data);
        
        // Check if the error is "Email not found"
        if (error.response.status === 404 && error.response.data.message === 'Email not found.') {
          setShowPopup(true);
        }
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
      }
    }
  };

  const handleAppleError = (error) => {
    console.error('Apple login failed:', error);
    console.error('Current URL:', window.location.origin);
    console.error('Redirect URI:', window.location.origin);
    // Handle error appropriately
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const demoCode = params.get('demo');
    if (!demoCode) return;

    const attemptDemoLogin = async () => {
      try {
        const response = await api.post('/api/company/demo-login', { inviteCode: demoCode });
        if (response.data?.token && response.data?.user && response.data?.company) {
          login(response.data.token, response.data.user, response.data.company);
          window.history.replaceState({}, document.title, window.location.pathname);
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Demo login failed:', err);
        setDemoRequestError(err?.response?.data?.message || 'Invalid or expired invite code');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    attemptDemoLogin();
  }, [login, navigate]);

  const handleDemoRequestSubmit = async (e) => {
    e.preventDefault();
    setDemoRequestError(null);
    if (!demoForm.name?.trim() || !demoForm.companyName?.trim() || !demoForm.email?.trim()) {
      setDemoRequestError('Name, company name, and email are required');
      return;
    }
    setDemoRequestLoading(true);
    try {
      await api.post('/api/demo/request', demoForm);
      setDemoRequestSuccess(true);
      setDemoForm({ name: '', companyName: '', email: '', phone: '', numberOfEmployees: '', state: '', howYouHeardAboutUs: '' });
      setTimeout(() => {
        setShowDemoRequestModal(false);
        setDemoRequestSuccess(false);
      }, 2500);
    } catch (err) {
      setDemoRequestError(err?.response?.data?.message || 'Failed to submit request');
    } finally {
      setDemoRequestLoading(false);
    }
  };

  // Debug logging
  /* console.log('Apple Sign-In Config:', {
    clientId: 'com.bloomnav.organisation',
    redirectURI: 'https://bloomnav.com/dashboard',
    currentURL: window.location.href
  }); */

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Welcome to BloomNav</h1>
        <p>Sign in to manage your business</p>
        
        <div className="login-buttons">
          <div className="google-login-button">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap
              theme="filled_black"
              text="signin_with"
              shape="rectangular"
            />
          </div>
          
          {/* Apple Sign-In - using existing App ID with web auth enabled */}
          {true && (
            <div className="apple-login-button">
              <AppleSignin
                authOptions={{
                  clientId: 'com.bloomnav.webapp', // You'll need to configure this with your Apple Developer account
                  scope: 'name email',
                  redirectURI: 'https://bloomnav.com/login', // Fixed for development testing
                  state: 'auth',
                  nonce: 'nonce',
                  usePopup: true,
                }}
                onSuccess={handleAppleSuccess}
                onError={handleAppleError}
                skipScript={false}
                iconProp={{ style: { color: 'white' } }}
                textProp={{
                  color: 'white',
                  fontSize: 12,
                 fontWeight: 'bold',
                }}
                className="apple-auth-btn"
                buttonExtraChildren="Continue with Apple"
              />
            </div>
          )}
          <div className="demo-login-button">
            <button type="button" onClick={() => setShowDemoRequestModal(true)} className="demo-button">Request Demo Access</button>
          </div>
        </div>

        {demoRequestError && !showDemoRequestModal && (
          <div className="account-not-found-popup__content" style={{ marginTop: 16, padding: 12, background: '#fef2f2', borderRadius: 8, color: '#dc2626' }} role="alert">
            {demoRequestError}
            <button type="button" onClick={() => setDemoRequestError(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }} aria-label="Dismiss">×</button>
          </div>
        )}
      </div>

      {showDemoRequestModal && (
        <div className="popup-overlay account-not-found-popup" role="dialog" aria-modal="true" aria-label="Request demo access">
          <div className="popup-content account-not-found-popup__content">
            <button
              type="button"
              className="account-not-found-popup__close"
              aria-label="Close"
              onClick={() => { setShowDemoRequestModal(false); setDemoRequestError(null); setDemoRequestSuccess(false); }}
            >
              &times;
            </button>
            {demoRequestSuccess ? (
              <>
                <h2>Request received</h2>
                <p>We&apos;ll email you an invite once approved. Check your inbox.</p>
              </>
            ) : (
              <>
                <h2>Request Demo Access</h2>
                <p>Fill in your details and we&apos;ll send you an invite to explore BloomNav.</p>
                <form onSubmit={handleDemoRequestSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
                  <input
                    type="text"
                    placeholder="Your name *"
                    value={demoForm.name}
                    onChange={(e) => setDemoForm((f) => ({ ...f, name: e.target.value }))}
                    required
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #d4d4d4' }}
                  />
                  <input
                    type="text"
                    placeholder="Company name *"
                    value={demoForm.companyName}
                    onChange={(e) => setDemoForm((f) => ({ ...f, companyName: e.target.value }))}
                    required
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #d4d4d4' }}
                  />
                  <input
                    type="email"
                    placeholder="Email *"
                    value={demoForm.email}
                    onChange={(e) => setDemoForm((f) => ({ ...f, email: e.target.value }))}
                    required
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #d4d4d4' }}
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={demoForm.phone}
                    onChange={(e) => setDemoForm((f) => ({ ...f, phone: e.target.value }))}
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #d4d4d4' }}
                  />
                  <select
                    value={demoForm.numberOfEmployees}
                    onChange={(e) => setDemoForm((f) => ({ ...f, numberOfEmployees: e.target.value }))}
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #d4d4d4' }}
                  >
                    <option value="">Number of employees</option>
                    {EMPLOYEE_COUNT_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="State / Region"
                    value={demoForm.state}
                    onChange={(e) => setDemoForm((f) => ({ ...f, state: e.target.value }))}
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #d4d4d4' }}
                  />
                  <select
                    value={demoForm.howYouHeardAboutUs}
                    onChange={(e) => setDemoForm((f) => ({ ...f, howYouHeardAboutUs: e.target.value }))}
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #d4d4d4' }}
                  >
                    <option value="">How did you hear about us?</option>
                    {HOW_HEARD_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  {demoRequestError && <div style={{ color: '#dc2626', fontSize: 14 }}>{demoRequestError}</div>}
                  <button
                    type="submit"
                    className="account-not-found-popup__signup-btn"
                    disabled={demoRequestLoading}
                  >
                    {demoRequestLoading ? 'Submitting...' : 'Submit Request'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {showPopup && (
        <div className="popup-overlay account-not-found-popup" role="dialog" aria-modal="true" aria-label="Account not found">
          <div className="popup-content account-not-found-popup__content">
            <button
              type="button"
              className="account-not-found-popup__close"
              aria-label="Close"
              onClick={() => setShowPopup(false)}
            >
              &times;
            </button>
            <h2>Account not found</h2>
            <p>It looks like you don't have an account yet. Create one to get started with BloomNav.</p>
            <button
              type="button"
              className="account-not-found-popup__signup-btn"
              onClick={() => navigate('/signup')}
            >
              Create Account
            </button>
            <button
              type="button"
              className="account-not-found-popup__dismiss"
              onClick={() => setShowPopup(false)}
            >
              Back to login
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login; 